import React, { useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";

import JSZip from "jszip";

import ErrorIcon from "@mui/icons-material/Error";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import MapPlaybackFileViewer from "../../mapPlayback/mapPlaybackFileViewer";

import useGetRequest from "../../hooks/useGetRequest";

import { getHoursMinutesAndSecondsFromIsoString } from "../../util";

const FlightPlaybackDialog = ({ flightPlaybackDialogUuid, setFlightPlaybackDialogUuid }) => {
    const [playbackTime, setPlaybackTime] = useState(null);

    const virtuosoRef = useRef(null);

    const { data, loading, error, setData } = useGetRequest(`api/map_playback/get/${flightPlaybackDialogUuid}`);

    const filteredEvents = useMemo(() => {
        if (!data?.events) return [];

        return data.events.filter(({ type }) => {
            return type !== "ASD_TRACK";
        });
    }, [data?.events]);

    const lastEventThatOccuredIndex = useMemo(() => {
        return filteredEvents.findLastIndex((e) => {
            return new Date(e.timestamp_iso_str).getTime() <= playbackTime;
        });
    }, [filteredEvents, playbackTime]);

    useEffect(() => {
        if (virtuosoRef.current) {
            virtuosoRef.current.scrollToIndex({ index: lastEventThatOccuredIndex, behavior: "smooth" });
        }
    }, [lastEventThatOccuredIndex]);

    const handleSelectedEventChange = (index) => {
        const newEvent = filteredEvents[index];
        if (!newEvent) return;

        const newEventTimeMs = new Date(newEvent.timestamp_iso_str).getTime();
        setPlaybackTime(newEventTimeMs);
    };
    const handleDownloadButtonClick = async () => {
        try {
            const zip = new JSZip();
            const jsonString = JSON.stringify(data);

            const fileName = `${data.playback_uuid}.json`;
            zip.file(fileName, jsonString);

            const zipContent = await zip.generateAsync({
                type: "blob",
                compression: "DEFLATE",
                compressionOptions: { level: 9 }
            });
            const url = window.URL.createObjectURL(zipContent);
            const link = document.createElement("a");
            link.href = url;

            const zipName = `${data.playback_uuid}.zip`;
            link.setAttribute("download", zipName);

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
        }
    };
    return (
        <Dialog open={flightPlaybackDialogUuid ? true : false} onClose={() => setFlightPlaybackDialogUuid("")} maxWidth={"md"} fullWidth>
            <DialogTitle component={"div"} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6" color="text.primary">
                        {"Flight Playback"}
                    </Typography>

                    <Tooltip title="Close" placement="left">
                        <IconButton onClick={() => setFlightPlaybackDialogUuid("")} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{ height: "375px" }}>
                    <MapPlaybackFileViewer
                        viewPlaybackFileData={data}
                        setViewPlaybackFileData={setData}
                        playbackTime={playbackTime}
                        setPlaybackTime={setPlaybackTime}
                        showToolbar={false}
                    />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6">Events</Typography>

                    {filteredEvents && filteredEvents.length > 0 && !loading && !error ? (
                        <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}>
                            <IconButton
                                size="small"
                                disabled={lastEventThatOccuredIndex === 0}
                                onClick={() => handleSelectedEventChange(lastEventThatOccuredIndex - 1)}
                            >
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>

                            <Typography variant="body1" color="text.secondary">
                                {lastEventThatOccuredIndex + 1} of {filteredEvents.length}
                            </Typography>

                            <IconButton
                                disabled={lastEventThatOccuredIndex === filteredEvents.length - 1}
                                size="small"
                                onClick={() => handleSelectedEventChange(lastEventThatOccuredIndex + 1)}
                            >
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ) : (
                        <></>
                    )}
                </Box>
            </DialogTitle>

            <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
                {filteredEvents && filteredEvents.length > 0 && !loading && !error ? (
                    <Virtuoso
                        ref={virtuosoRef}
                        data={filteredEvents}
                        style={{ height: filteredEvents.length * 70 }}
                        itemContent={(i, event) => {
                            const selected = i === lastEventThatOccuredIndex ? true : false;
                            return (
                                <Box
                                    onClick={() => handleSelectedEventChange(i)}
                                    sx={{ position: "relative", display: "flex", flexDirection: "column" }}
                                    data-testid={`event-${i}`}
                                >
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            left: 0,
                                            top: "12px",
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center"
                                        }}
                                    >
                                        {selected ? <RadioButtonCheckedIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                                        {i !== filteredEvents.length - 1 ? <Box sx={{ height: "100%", width: "2px", backgroundColor: "white" }} /> : <></>}
                                    </Box>

                                    <Box sx={{ ml: 4, p: 1, border: selected ? "1px solid white" : "unset", borderRadius: 2 }}>
                                        <Box
                                            sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", width: "100%" }}
                                        >
                                            <Typography variant="body1">{event.title}</Typography>

                                            <Typography variant="body2" color="text.secondary">
                                                {getHoursMinutesAndSecondsFromIsoString(event.timestamp_iso_str)}
                                            </Typography>
                                        </Box>

                                        <Typography variant="body2" color="text.secondary" whiteSpace="pre-wrap">
                                            {event.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        }}
                    />
                ) : loading ? (
                    [1, 2, 3].map((index) => (
                        <Box key={index} sx={{ display: "flex", flexDirection: "column", p: 1 }}>
                            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50%" }} />
                            <Skeleton variant="text" sx={{ fontSize: "1.25rem", width: "75%" }} />
                        </Box>
                    ))
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center", width: "75%", margin: "auto" }}>
                        <ErrorIcon color="error" fontSize="large" />

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography variant="body1">Something went wrong</Typography>

                            <Typography variant="body2" color="text.secondary">
                                An error occured while loading this replay. Please try again later.
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {filteredEvents && filteredEvents.length > 0 && !loading && !error ? (
                    <Button size="small" onClick={handleDownloadButtonClick}>
                        {"Download Playback File"}
                    </Button>
                ) : (
                    <></>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default FlightPlaybackDialog;
